// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import Plot from 'react-plotly.js';

interface CostBreakdown {
  directMedicalCost: number;
  programmeDeliveryCost: number;
  productivityCost: number;
  adverseEventCost: number;
}

interface CostCompositionProps {
  costBreakdown: CostBreakdown;
  title?: string;
}

const COST_COMPONENTS = [
  { key: 'directMedicalCost', label: 'Direct medical costs', shortLabel: 'Direct medical', color: '#4C8DFF' }, // Secondary - clearly differentiated blue
  { key: 'programmeDeliveryCost', label: 'Programme delivery costs', shortLabel: 'Programme delivery', color: '#5EDFCB' }, // Main segment - dominant green-turquoise
  { key: 'productivityCost', label: 'Productivity costs', shortLabel: 'Productivity', color: '#6A5BE8' }, // Tertiary - distinct purple
  { key: 'adverseEventCost', label: 'Adverse event costs', shortLabel: 'Adverse event', color: '#8BB0FF' }, // Quaternary - lighter blue for small slices
] as const;

/**
 * Format a number with appropriate scale (K, M, B) based on magnitude
 * Returns the formatted string and the divisor used for scaling
 */
function formatScaledValue(value: number): { formatted: string; divisor: number; unit: string } {
  const absValue = Math.abs(value);
  
  if (absValue >= 1_000_000_000) {
    return {
      formatted: `€${(value / 1_000_000_000).toFixed(2)}B`,
      divisor: 1_000_000_000,
      unit: 'B'
    };
  } else if (absValue >= 1_000_000) {
    return {
      formatted: `€${(value / 1_000_000).toFixed(2)}M`,
      divisor: 1_000_000,
      unit: 'M'
    };
  } else if (absValue >= 1_000) {
    return {
      formatted: `€${(value / 1_000).toFixed(2)}K`,
      divisor: 1_000,
      unit: 'K'
    };
  } else {
    return {
      formatted: `€${value.toFixed(2)}`,
      divisor: 1,
      unit: ''
    };
  }
}

/**
 * Cost Composition visualization showing breakdown of vaccine-related costs
 * Displays both pie chart (percentage share) and bar chart (absolute amounts)
 */
export const CostComposition: React.FC<CostCompositionProps> = ({
  costBreakdown,
  title = 'Cost composition',
}) => {

  const components = COST_COMPONENTS.map((comp) => ({
    ...comp,
    value: costBreakdown[comp.key],
  }));

  const total = components.reduce((sum, comp) => sum + comp.value, 0);
  
  // Determine the appropriate scale based on the total value
  const totalScale = formatScaledValue(total);

  return (
    <div className="vaccine-demo__chart-container">
      <div className="vaccine-demo__chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div className="vaccine-demo__chart-title" style={{ color: '#f8fafc', fontSize: '1.1rem', fontWeight: 600 }}>{title}</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        <div style={{ minHeight: 400, flex: '1.2 1 320px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Plot
            data={[
              {
                labels: components.map((comp) => comp.label),
                values: components.map((comp) => comp.value),
                type: 'pie',
                hole: 0.45, // Slightly larger hole for better proportions
                marker: { 
                  colors: components.map((comp) => comp.color),
                  line: {
                    color: '#ffffff',
                    width: 2,
                  },
                },
                // Show percent for all slices - small slices may be hard to read but hover shows details
                textinfo: 'percent',
                textposition: 'inside',
                textfont: { color: '#1e293b', size: 13, family: 'Inter, sans-serif' },
                hovertemplate: '%{label}<br>%{percent} (€%{value:,.0f})<extra></extra>',
              },
            ]}
            layout={{
              autosize: true,
              height: 420, // Increased height for larger donut
              margin: { l: 20, r: 20, t: 20, b: 80 },
              showlegend: true,
              legend: {
                x: 0.5,
                y: -0.15,
                xanchor: 'center',
                yanchor: 'top',
                orientation: 'h',
                font: { color: '#e2e8f0', size: 12, family: 'Inter, sans-serif' },
                bgcolor: 'rgba(0,0,0,0)',
                bordercolor: 'rgba(0,0,0,0)',
                itemwidth: 35,
                tracegroupgap: 12,
              },
              annotations: [
                {
                  text: 'Total Cost',
                  x: 0.5,
                  y: 0.58,
                  font: { size: 12, color: '#94a3b8', family: 'Inter, sans-serif' },
                  showarrow: false,
                  align: 'center',
                  xref: 'paper',
                  yref: 'paper',
                  opacity: 0.7,
                },
                {
                  text: totalScale.formatted,
                  x: 0.5,
                  y: 0.48,
                  font: { size: 22, color: '#f1f5f9', family: 'Inter, sans-serif' },
                  showarrow: false,
                  align: 'center',
                  xref: 'paper',
                  yref: 'paper',
                },
              ],
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              font: { color: '#e2e8f0', size: 12 },
            }}
            config={{ displaylogo: false, responsive: true }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>

        {/* Bar Chart */}
        <div style={{ minHeight: 400, flex: '0.9 1 280px', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Plot
            data={components.map((comp, index) => {
              const percent = total > 0 ? ((comp.value / total) * 100).toFixed(1) : '0.0';
              const scaledValue = comp.value / totalScale.divisor;
              return {
                x: [index],
                y: [scaledValue],
                name: comp.label,
                type: 'bar',
                marker: { 
                  color: comp.color,
                  opacity: comp.value > 0 ? 1 : 0.05, // Reduced opacity for zero values
                  line: {
                    color: comp.color,
                    width: 1,
                  },
                },
                text: [`${percent}%`],
                textposition: 'middle center',
                textangle: 0, // Force horizontal text
                textfont: { color: '#1e293b', size: 13, family: 'Inter, sans-serif' },
                hovertemplate: `${comp.label}: €%{y:.2f}${totalScale.unit} (${percent}%)<extra></extra>`,
              };
            })}
            layout={{
              barmode: 'group',
              autosize: true,
              height: 420, // Match donut height
              margin: { l: 60, r: 20, t: 20, b: 60 },
              yaxis: {
                title: { 
                  text: `Total cost (€${totalScale.unit})`,
                  font: { color: '#e2e8f0', size: 13, family: 'Inter, sans-serif' },
                },
                tickformat: ',.1f',
                tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
                gridcolor: '#1f2937',
              },
              xaxis: {
                title: { text: '' },
                tickangle: -20, // Less tilted for better readability
                tickfont: { color: '#e2e8f0', size: 11, family: 'Inter, sans-serif' },
                tickmode: 'array',
                tickvals: components.map((_, i) => i),
                ticktext: components.map((comp) => comp.shortLabel),
                type: 'category',
              },
              showlegend: false,
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              font: { color: '#e2e8f0', size: 12 },
            }}
            config={{ displaylogo: false, responsive: true }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
      </div>
    </div>
  );
};

export default CostComposition;
